/** BuyDrop buyer app — iOS App Store (HTTPS for QR codes, copy/paste, desktop) */
export const APP_STORE_BUYDROP_URL =
  'https://apps.apple.com/us/app/buydrop-local-delivery/id6762064770';

/**
 * iOS App Store deep link — opens the App Store app when tapped in Safari / in-app browser on iPhone & iPad.
 * Use for <a href> on tap targets; keep {@link APP_STORE_BUYDROP_URL} for QR payloads.
 */
export const APP_STORE_BUYDROP_ITMS_URL =
  'itms-apps://apps.apple.com/app/id6762064770';

/** BuyDrop buyer app — Google Play (override via REACT_APP_BUYER_ANDROID_STORE_URL) */
export const PLAY_STORE_BUYDROP_URL = (
  process.env.REACT_APP_BUYER_ANDROID_STORE_URL?.trim() ||
  'https://play.google.com/store/apps/details?id=com.buydrop.buyer'
).trim();

/** Opens the Play Store app when tapped on Android */
export const PLAY_STORE_BUYDROP_MARKET_URL = 'market://details?id=com.buydrop.buyer';

/** Single QR — /app redirects phones straight to the App Store or Google Play (server-side). */
export const BUYER_APP_QR_URL = `${
  (process.env.REACT_APP_PUBLIC_URL?.trim() || 'https://www.buydrop.org').replace(/\/$/, '')
}/app`;

/** BuyDrop Seller app — iOS App Store */
export const APP_STORE_SELLER_URL = (
  process.env.REACT_APP_DRIVER_IOS_STORE_URL?.trim() ||
  'https://apps.apple.com/us/app/buy-drop-seller/id6761207586'
).trim();

export const APP_STORE_SELLER_ITMS_URL =
  'itms-apps://apps.apple.com/app/id6761207586';

/** BuyDrop Seller app — Google Play (override via REACT_APP_DRIVER_ANDROID_STORE_URL) */
export const PLAY_STORE_SELLER_URL = (
  process.env.REACT_APP_DRIVER_ANDROID_STORE_URL?.trim() ||
  'https://play.google.com/store/apps/details?id=com.buydrop.driver'
).trim();

export const PLAY_STORE_SELLER_MARKET_URL = 'market://details?id=com.buydrop.driver';

/** Single QR — /sell-app redirects phones to the seller App Store or Google Play (server-side). */
export const SELLER_APP_QR_URL = `${
  (process.env.REACT_APP_PUBLIC_URL?.trim() || 'https://www.buydrop.org').replace(/\/$/, '')
}/sell-app`;

function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  // iPadOS 13+ can report as Mac with touch
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  return false;
}

function isAndroidDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent || '');
}

/** Tap / click: App Store app on iPhone & iPad, HTTPS elsewhere. */
export function buyerAppStoreHref(): string {
  return isIOSDevice() ? APP_STORE_BUYDROP_ITMS_URL : APP_STORE_BUYDROP_URL;
}

/** Tap / click: Play Store app on Android, HTTPS elsewhere. */
export function buyerPlayStoreHref(): string {
  return isAndroidDevice() ? PLAY_STORE_BUYDROP_MARKET_URL : PLAY_STORE_BUYDROP_URL;
}

/** Primary store link for the current device (buyer app). */
export function buyerAppDownloadHref(): string {
  if (isAndroidDevice()) return buyerPlayStoreHref();
  return buyerAppStoreHref();
}

/** Props for <a> — same-tab on iOS (itms), new tab + noopener for HTTPS on desktop. */
export function buyerAppStoreAnchorProps(): {
  href: string;
  target: string;
  rel?: string;
} {
  const href = buyerAppStoreHref();
  if (href.startsWith('itms-apps:')) {
    return { href, target: '_self' };
  }
  return { href, target: '_blank', rel: 'noopener noreferrer' };
}

/** Props for Google Play buyer app link. */
export function buyerPlayStoreAnchorProps(): {
  href: string;
  target: string;
  rel?: string;
} {
  const href = buyerPlayStoreHref();
  if (href.startsWith('market:')) {
    return { href, target: '_self' };
  }
  return { href, target: '_blank', rel: 'noopener noreferrer' };
}

/** Tap / click: App Store app on iPhone & iPad, HTTPS elsewhere (seller app). */
export function sellerAppStoreHref(): string {
  return isIOSDevice() ? APP_STORE_SELLER_ITMS_URL : APP_STORE_SELLER_URL;
}

/** Tap / click: Play Store app on Android, HTTPS elsewhere (seller app). */
export function sellerPlayStoreHref(): string {
  return isAndroidDevice() ? PLAY_STORE_SELLER_MARKET_URL : PLAY_STORE_SELLER_URL;
}

export function sellerAppStoreAnchorProps(): {
  href: string;
  target: string;
  rel?: string;
} {
  const href = sellerAppStoreHref();
  if (href.startsWith('itms-apps:')) {
    return { href, target: '_self' };
  }
  return { href, target: '_blank', rel: 'noopener noreferrer' };
}

export function sellerPlayStoreAnchorProps(): {
  href: string;
  target: string;
  rel?: string;
} {
  const href = sellerPlayStoreHref();
  if (href.startsWith('market:')) {
    return { href, target: '_self' };
  }
  return { href, target: '_blank', rel: 'noopener noreferrer' };
}
